<template>
  <div id="div-pei-signature">
    <ValidationObserver ref="formPeiSignatureFormObserver" tag="form" validate>
      <SectionTitle
        icon="fas fa-pen-nib"
        content="Firmar PEI"
        container-class="tag-badge-success mt-5 mb-4"
      />
      <div class="d-flex flex-row  py-3 px-4 sub-section-info">
        <div class="col-4 pl-0 pt-0">
          <div>
            <label class="title">Fecha Discusión COMPU:</label>
          </div>
          <ValidationProvider
            v-slot="{ errors }"
            mode="eager"
            name="(Fecha Discusión COMPU:)"
            rules="required"
          >
            <CustomDatePicker
              v-model="date"
              :custom-error-msg="resources['R03200138']"
              :error="errors[0]"
              :max-date="maxSignDate"
              :disabled="isReadOnly"
              @onSelectedDate="onSelectedDate"
            />
          </ValidationProvider>
        </div>
        <div v-if="isReadOnly && signerUser" class="col-2 pt-0">
          <div>
            <label class="title">Firmado por:</label>
          </div>
          <p class="font-weight-lighter">
            {{ signerUser }}
          </p>
        </div>
        <div v-if="!isReadOnly" class="d-flex flex-row mt-4 ml-auto">
          <ActionButton
            name="btnPeiSign"
            i="fas fa-pen"
            variant="success m-2 pl-3 pr-3"
            @click="setPeiSignature"
          >
            Firmar
          </ActionButton>
          <ActionButton
            name="btnCancellPeiSign"
            i="fas fa-times-circle"
            variant="outline-danger m-2"
            @click="closeSection"
          >
            Cancelar
          </ActionButton>
        </div>
      </div>
      <div
        v-if="therapies && therapies.length > 0"
        class="d-flex flex-row justify-content-between py-3 px-4 sub-section-info mt-1"
      >
        <div class="col-12 px-0 pt-0">
          <div>
            <label class="title">Certificación del Referido:</label>
          </div>
          <ValidationProvider v-slot="{ errors }" rules="required">
            <CustomCheckboxSingleOption
              v-model="acceptTerms"
              :disabled="isReadOnly"
              :option-text="therapiesText"
              :error="errors[0]"
              :custom-error-msg="resources['R03200142']"
            />
          </ValidationProvider>
        </div>
      </div>
      <div v-if="showOutOfTermSection" class="sub-section-info py-3 px-4 mt-1">
        <div>
          <div><label class="title">PEI Inicial Fuera de Término</label></div>
          <div class="w-100">
            <Error
              v-if="alertOutOfTerm && !isReadOnly"
              variant="mt-2"
              is-alert-mode
              :message="alertOutOfTerm"
            />
          </div>
          <div class="row ml-0 mr-0">
            <div v-if="isReadOnly" class="col-4 px-3 pt-2">
              <label>Razón:</label>
              <p class="font-weight-lighter">
                {{ selectedReason.name }}
              </p>
            </div>
            <div v-if="isReadOnly && hasExplanation" class="col px-3 pt-2">
              <label>Explique:</label>
              <p class="font-weight-lighter">
                {{ reasonExplanation }}
              </p>
            </div>
            <div v-if="!isReadOnly" class="col-12 px-0 py-0">
              <ValidationProvider
                v-slot="{ errors }"
                mode="eager"
                rules="required"
              >
                <CustomDropDown
                  v-model="selectedReason"
                  label="Razón:"
                  :disabled="isReadOnly"
                  fieldkey="id"
                  fieldname="name"
                  placeholder="Seleccione"
                  :options="reasonList"
                  :on-selection-change="cleanFields"
                  :custom-error-msg="resources['R03200139']"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>
            <div
              v-if="!isReadOnly"
              class="col-12 pt-0  mt-2 px-0 mx-0 explanation-cta"
            >
              <ValidationProvider
                v-if="hasExplanation"
                v-slot="{ errors }"
                mode="eager"
                rules="required"
              >
                <CustomTextArea
                  v-model="reasonExplanation"
                  :disabled="isReadOnly"
                  label="Explique:"
                  :maxlength="300"
                  :rows="1"
                  :custom-error-msg="resources['R03200140']"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>
    <contact-info-modal ref="contactInfoModal" @save="saveModal" />
  </div>
</template>

<script>
import ActionButton from '@/components/common/ActionButton';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { SectionTitle } from '@/components/pei/shared';
import tardyRegistrationReasons from '@/utils/constants/pei/peiTardyRegistrationReasons.js';
import ContactInfoModal from '@/components/common/CustomContactInfoModal';
import {
  CustomDatePicker,
  CustomDropDown,
  CustomTextArea,
  Error,
} from '@/components/common';
import CustomCheckboxSingleOption from '@/components/common/CustomCheckboxSingleOption';
import resourcesService from '@/services/resources.js';
import moment from 'moment';
import store from '@/store/index';
import PeiService from '@/services/pei/general';
import StudentService from '@/services/student';
import PeiStatus from '@/utils/constants/pei/peiStatus';
import StudentStatuses from '@/utils/constants/student/studentStatuses';
import RegStudentTardinessLogLocation from '@/utils/constants/reg/regStudentTardinessLogLocation';
import LoadingMixin from '@/mixin/loading.mixin';
import mixAlert from '@/mixin/alert.mixin';

const PeiSignedStatus = 'Firmado';

export default {
  components: {
    ActionButton,
    ValidationObserver,
    ValidationProvider,
    SectionTitle,
    CustomDatePicker,
    CustomDropDown,
    CustomTextArea,
    Error,
    ContactInfoModal,
    CustomCheckboxSingleOption,
  },
  mixins: [LoadingMixin, mixAlert],
  props: {
    studentSie: {
      type: Number,
      default: null,
    },
    isEducationalCompensatoryServicesCompleted: {
      type: Boolean,
      default: null,
    },
    peiId: {
      type: String,
      default: null,
    },
    generalClosedDate: {
      type: Date,
      default: null,
    },
  },
  data() {
    return {
      date: null,
      resources: [],
      showOutOfTermSection: false,
      alertOutOfTerm: null,
      reasonList: null,
      selectedReason: null,
      reasonExplanation: null,
      tardyRegistrationReasons: tardyRegistrationReasons,
      therapies: [],
      acceptTerms: undefined,
      submitted: false,
      therapiesText: '',
      student: null,
      createdDate: null,
      signerUser: '',
    };
  },
  async mounted() {
    await this.loadResources();
    await this.loadReasons();
    await this.getTherapies();
    await this.loadStudent();
    await this.getPeiSignDetail();
  },
  watch: {
    visible(newValue) {
      this.showSection = newValue;
    },
    isReadOnly(newValue) {
      if (newValue) {
        this.acceptTerms = true;
      }
    },
  },
  computed: {
    maxSignDate() {
      return new Date();
    },
    selectedPei() {
      return this.$store.state.pei;
    },
    peiStatus() {
      return this.selectedPei.peiStatusCode;
    },
    isReadOnly() {
      if (this.peiStatus == parseInt(PeiStatus.Firmado)) return true;
      return false;
    },
    regStudentId() {
      return this.student.mipeStudent.regStudentId;
    },
    hasExplanation() {
      return (
        this.selectedReason &&
        this.selectedReason.id == tardyRegistrationReasons.Otros
      );
    },
  },
  methods: {
    cleanFields() {
      this.reasonExplanation = null;
    },
    onSelectedDate(val) {
      if (val && this.student && this.createdDate) {
        let signDate = moment(val);
        let regDate = moment(this.createdDate);
        let isOutOfTerm = signDate.diff(regDate, 'days') > 60;
        this.showOutOfTermSection =
          this.student.regStudentPostRegistryCount == 0 &&
          this.student.mipeStudent.studentStatusCode ==
            StudentStatuses.Elegible &&
          this.student.conversionFlag != '1' &&
          !this.student.mipeStudent.peiInitialDate &&
          isOutOfTerm;
      } else {
        this.showOutOfTermSection = false;
      }
    },
    async validateAtEquipmentCancelation(peiId) {
      const { data } = await this.$ApiGet(
        `api/pei/validateatequipmentcancelation/${peiId}`
      );
      return data;
    },
    async setPeiSignature() {
      if (!this.isEducationalCompensatoryServicesCompleted) {
        this.ShowToast('', this.resources['R9260056'], 'error');
        return;
      }
      this.submitted = true;
      let isValid = await this.$refs.formPeiSignatureFormObserver.validate();
      if (!isValid) return;

      const hasAtEquipmentCancelation = await this.validateAtEquipmentCancelation(
        this.peiId
      );
      if (hasAtEquipmentCancelation) {
        var canContinue = await this.ShowCancelModal(
          'Atención',
          `Al proceder con la Firma del PEI ${this.peiId} el equipo de Asistencia Tecnológica del estudiante que haya identificado que no continuará su uso sera removido del PEI y se procederá a notificar al CAAT a través de la garantía Inventario Equipos AT que estos serán devueltos.`,
          'Si',
          'No'
        );

        if (!canContinue) {
          return;
        }
      }

      await this.$refs.contactInfoModal.open();
    },
    closeSection() {
      this.$store.dispatch('setVisiblePeiSignSection', false);
    },
    async loadStudent() {
      let self = this;
      await self
        .$ApiGet('/api/student/getstudentdetails', {
          studentSie: store.getters.SelectedStudent.studentId,
        })
        .then(async (response) => {
          self.student = response.data;
          self.createdDate = self.student.mipeStudent.registrationDate;
        });
    },
    async loadReasons() {
      let self = this;
      let promise = new Promise((resolve) => {
        self
          .$ApiGet(`/api/signature/initialtardinessreasons`)
          .then((res) => {
            self.reasonList = res.data;
            resolve(res.data);
          })
          .catch((err) => {
            self.response = err;
          });
      });
      return promise;
    },
    async loadResources() {
      let resourceCodes = [
        'R03200138',
        'R03200139',
        'R03200140',
        'R03200141',
        'R03200142',
        'R9260056',
      ];
      this.resources = await resourcesService.LoadValidationMessages(
        resourceCodes
      );

      // Out of Term Alert message
      if (this.resources.R03200141) {
        let dateToShow = moment(this.createdDate)
          .locale('es')
          .format('DD/MMM/YYYY');
        this.alertOutOfTerm = this.resources.R03200141.replace(
          '</date>',
          dateToShow
        );
      }
    },
    async getTherapies() {
      let self = this;

      self
        .$ApiGet('/api/pei/getpeitherapies/' + this.peiId)
        .then((response) => {
          if (response.data) {
            if (response.data.length == 0) {
              return;
            }

            self.therapies = response.data.map((theraphy) => theraphy.name);
            self.therapiesText =
              'Certifico por este medio que los referidos a la provisión del servicio de intervención a las terapias: <strong>' +
              self.therapies.join(', ') +
              '.</strong> Se encuentran en el expediente físico del estudiante firmado por todas las partes involucradas.';
          }
        })
        .catch((error) => {
          if (error && error.response && error.response.data) {
            self.ShowToast('', error.response.data.message, 'error');
          } else {
            self.ShowToast(
              '',
              'Ha ocurrido un error mientras se procesaba la información.',
              'error'
            );
          }
        });
    },
    async saveModal(params) {
      let self = this;

      let response = await self.$ApiGet(
        `/api/pei/validateinitialpeicopy?peiId=${this.peiId}`
      );

      response = response?.data ?? {};

      let hasRequiredFuturePeiCopy = false;
      if (response?.showAlert) {
        hasRequiredFuturePeiCopy = await self.ShowConfirm(
          'Confirmación',
          `¿Desea crear una copia de PEI para el año ${response.nextYear.year}?`
        );
      } else if (response?.hasPeiForNextYear) {
        self.ShowAlertModal(
          'Confirmación',
          `No es posible crear la copia, ya que cuenta con un PEI en Borrador`
        );
      }

      let contactInfo = {
        contactName: params.contactname,
        contacteMail: params.contactemail,
        emailSuppliedFlag: params.emailsuppliedflag,
        contactTelephone: params.contacttelephone,
        contactTelephoneProviderCode: params.contacttelephoneprovidercode,
        regStudentId: params.regstudentid,
        contactRelation: params.contactrelation,
        receiveMessageTextFlag: params.receiveMessageTextFlag,
      };
      await StudentService.updateStudentContactInformation(contactInfo);

      let studentTardinessLog = null;

      if (
        this.showOutOfTermSection &&
        this.student.mipeStudent != null &&
        this.regStudentId != null
      ) {
        let regStudentId = this.regStudentId.replace('-', '');
        studentTardinessLog = {
          regStudentId: parseInt(regStudentId),
          tardyRegistrationReasonId: this.selectedReason.id,
          tardinessComment: this.reasonExplanation,
          tardynessLocationId: RegStudentTardinessLogLocation.InitialPEI,
        };
      }

      let signPei = {
        peiId: this.peiId,
        signedDate: this.date,
        studentTardinessLog: studentTardinessLog,
        hasRequiredFuturePeiCopy: hasRequiredFuturePeiCopy,
      };

      try {
        const { data } = await PeiService.signPei(signPei);
        this.closeSection();

        this.$store.dispatch('setSelectedPeiStatus', PeiStatus.Firmado);
        this.$store.dispatch(
          'setSelectedPeiStatusDescription',
          PeiSignedStatus
        );
        this.$store.dispatch('setVisiblePeiSignSection', true);

        const details = await this.getPeiSignDetail();

        this.$emit('signed');

        try {
          await PeiService.disableReferralWithServicesRemovedInCurrentPei(
            details
          );
        } catch (error) {
          this.ShowToast('', error.response.data.message, 'error');
        }

        if (!!data.evaluationDiscussionId) {
          this.$router.push({
            name: 'EvaluationDiscussionAT',
            params: { referralId: data.evaluationDiscussionId },
          });
        } else if (!!data.referralDiscussionSchoolId) {
          this.$router.push({
            name: 'ReferralATDiscussionSchool',
            params: { referralId: data.referralDiscussionSchoolId },
          });
        }
      } catch (error) {
        this.ShowToast('', error.response.data.message, 'error');
      }
    },
    async getPeiSignDetail() {
      let detailResponse;
      if (this.isReadOnly) {
        detailResponse = await PeiService.getPeiSignDetail(this.peiId);
        if (detailResponse && detailResponse.data) {
          let peiSignDetail = detailResponse.data;
          this.date = new Date(peiSignDetail.signedDate);
          this.signerUser = peiSignDetail.signerUser;
          this.acceptTerms = true;

          let responseTardinessLog = await StudentService.getTardinessLogs(
            this.regStudentId.replace('-', '')
          );
          if (responseTardinessLog && responseTardinessLog.data) {
            let tardinessLog = responseTardinessLog.data;

            this.selectedReason = this.reasonList.find(
              (a) => a.id == tardinessLog.tardyRegistrationReasonId
            );
            this.reasonExplanation = tardinessLog.tardinessComment;
            this.showOutOfTermSection = true;
          }

          this.$store.dispatch(
            'setSelectedPeiStatusDescription',
            peiSignDetail.peiStatusDescription
          );
        }
      }
      return detailResponse?.data;
    },
  },
};
</script>

<style scoped>
.sub-section-info {
  background: #f2f2f2 0% 0% no-repeat padding-box;
  border-radius: 4px;
}

.title {
  font: Bold 18px/28px Lato;
}
.explanation-cta >>> textarea {
  min-height: 38px;
}
#div-pei-signature >>> .modal {
  background: none;
}
</style>
